import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
	apiInstance,
	FileParameter,
	MeetingListItem,
	MeetingListRequest,
	MeetingRecordingSettings,
} from '@api';

import {
	MeetingListTable,
	Pagination,
	IconButton,
	Skeleton,
	Title,
	AppPageLayout,
	Space,
	MeetingAction,
} from '@components';

import {
	useMeetingsStoreContext, useUserStoreContext,
} from '@store';

import {
	PlusIconSlim,
} from '@icons';

import './MeetingListPage.scss';
import { setPageTitle, useModal, UserRights } from '@common';
import { CreateMeetingModal, DeleteMeetingModal, EditMeetingRecSettingsModal, MeetingInRecModal, NewMeetingInfo } from '@modals';
import { INotifiedPage } from '../../common';

const testClient = apiInstance;

function MeetingListPage(props: INotifiedPage): JSX.Element {
	const { registerNotificationHandler, unRegisterNotificationHandler } = props;

	const { state: UserStoreState } = useUserStoreContext();

	const
		[meetingsData, setMeetingsData] = useState<MeetingListItem[]>(null),
		[recordingInfo, setRecordingInfo] = useState<MeetingListItem>(null),
		[deleteMeetingInfo, setDeleteMeetingInfo] = useState<MeetingListItem>(null),
		[recordingInfoId, setRecordingInfoId] = useState<string>(null),
		[editMeetingRecSettingsId, setEditMeetingRecSettingsId] = useState<string>(null),
		[totalMeetingsCount, setTotalMeetingsCount] = useState<number>(0),
		[isDeveloperView, setIsDeveloperView] = useState<boolean>(false),
		[visitedMeetingIds,] = useState<string[]>(JSON.parse(localStorage.getItem('visitedMeetingIds')) as string[] || []),
		{ dispatch, state: meetingsStoreState } = useMeetingsStoreContext();

	const [meetingsDataBusy, setMeetingsDataBusy] = useState<boolean>(false);

	const fetchMeetingsData = useCallback(async () => {
		const meetingListRequest = new MeetingListRequest({
			paginationQuery: meetingsStoreState.paginationQuery,
			search: meetingsStoreState.query,
			showOrganizations: meetingsStoreState.showOrganizations,
		});

		let awaiting = true;
		setTimeout(() => {
			awaiting && setMeetingsDataBusy(awaiting);
		}, 500); // Show progress bar only if request takes more than timeout interval
		const meetingList = await testClient.list(meetingListRequest);
		awaiting = false;
		setMeetingsDataBusy(false);
		setMeetingsData(meetingList.meetings);
		setTotalMeetingsCount(meetingList.paginationResponce.totalPages);

		if (recordingInfoId) {
			const meetingInfo = meetingList.meetings.filter(m => m.id == recordingInfoId);
			if (meetingInfo && meetingInfo.length ) {
				const newMeetingInfo = meetingInfo[0];
				setRecordingInfo(newMeetingInfo);
			}
		}
		
	}, [setRecordingInfo, recordingInfoId, setMeetingsDataBusy, meetingsStoreState]);
		
	useEffect(() => {
		fetchMeetingsData();
		// openCreateMeedingModal(); // TODO FOR DEBUG Only
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meetingsStoreState]);

	useEffect(() => {
		setPageTitle('');

		const notifCallback = () => {
			fetchMeetingsData();
		}

		registerNotificationHandler("MeetingStatusChange", notifCallback);
		registerNotificationHandler("AgentStatusChange", notifCallback);

		return () => {
			unRegisterNotificationHandler("MeetingStatusChange", notifCallback);
			unRegisterNotificationHandler("AgentStatusChange", notifCallback);
		}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleKeyDown = (ev) => {	
			if (ev.key == 'F9' && UserStoreState.rights.includes(UserRights.developer) && !isDeveloperView) {
				setIsDeveloperView(true);
				dispatch({
					type: 'SET_SHOW_ORGANIZATIONS',
					payload: true,
				});
			}
		};
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		}
	}, [setIsDeveloperView, isDeveloperView, UserStoreState, dispatch]);

	const onPageChange = useCallback((newCurrentPage: number): void => {
		dispatch({
			type: 'SET_PAGE_NUMBER',
			payload: newCurrentPage,
		});
	}, [dispatch]);

	const canCreateMeeting = true;

	const {
		isModalOpen: isRecordingInfoModalOpen,
		closeModal: closeRecordingInfoModal,
		openModal: openRecordingInfoModal,
	} = useModal();

	const {
		isModalOpen: isCreateMeeingModalOpen,
		closeModal: closeCreateMeetingModal,
		openModal: openCreateMeedingModal,
	} = useModal();

	const {
		isModalOpen: isEditMeetingRecSettingsModalOpen,
		closeModal: closeEditMeetingRecSettings,
		openModal: openEditMeetingRecSettings,
	} = useModal();

	const {
		isModalOpen: isDeleteMeetingModalOpen,
		closeModal: closeDeleteMeetingModal,
		openModal: openDeleteMeetingModal,
	} = useModal();

	const onMeetingCreate = useCallback(async (newMeetingInfo: NewMeetingInfo) => {

		const recordingFile: FileParameter = !newMeetingInfo.isPlannedRecording ? {
			fileName: newMeetingInfo.recordingFile.name,
			data: newMeetingInfo.recordingFile
		} : null;

		await apiInstance.createFromUI(
			newMeetingInfo.id,
			newMeetingInfo.subject, 
			newMeetingInfo.startDateUtc, 
			newMeetingInfo.endDateUtc, 
			newMeetingInfo.location, 
			newMeetingInfo.isPlannedRecording,
			recordingFile,
			newMeetingInfo.plannedAgentRegId || "",
			JSON.stringify(newMeetingInfo.plannedAgentSeetings) || ""
			);
			
		await fetchMeetingsData();

	}, [fetchMeetingsData]);

	const onMeetingAction = useCallback((meeting: MeetingListItem, action: MeetingAction) => {
		switch (action) {
			case MeetingAction.record:
				apiInstance.startMeetingRecording(meeting.id);
				setRecordingInfo(meeting);
				setRecordingInfoId(meeting.id);
				openRecordingInfoModal();
				break;

			case MeetingAction.recording_info:
				setRecordingInfo(meeting);
				setRecordingInfoId(meeting.id);
				openRecordingInfoModal();
				break;

			case MeetingAction.pause_recording:
				apiInstance.pauseMeetingRecording(meeting.id);
				break;

			case MeetingAction.resume_recording:
				apiInstance.resumeMeetingRecording(meeting.id);
				break;

			case MeetingAction.stop_recording:
				apiInstance.stopMeetingRecording(meeting.id);
				setRecordingInfo(null);
				setRecordingInfoId(null);
				break;

			case MeetingAction.record_settings:
				setEditMeetingRecSettingsId(meeting.id);
				openEditMeetingRecSettings();
				break;

			case MeetingAction.delete:
				setDeleteMeetingInfo(meeting);
				openDeleteMeetingModal();
				break;
		
			default:
				break;
		}
	}, [setRecordingInfo, openRecordingInfoModal, openEditMeetingRecSettings,
		setDeleteMeetingInfo, setRecordingInfoId, setEditMeetingRecSettingsId,
		openDeleteMeetingModal]);

	const recordingInfoModalView = useMemo(() => {
		return isRecordingInfoModalOpen && (
			<MeetingInRecModal
				meetingInfo={recordingInfo}
				closeModal={closeRecordingInfoModal}
				onMeetingAction={onMeetingAction}
			/>
		);
	}, [isRecordingInfoModalOpen, onMeetingAction, recordingInfo,
		closeRecordingInfoModal])

	const uploadFileModalView = useMemo((): JSX.Element => {
		return isCreateMeeingModalOpen && (
			<CreateMeetingModal
				mode='upload'
				closeModal={closeCreateMeetingModal}
				onSubmit={onMeetingCreate}
			/>
		);
	}, [isCreateMeeingModalOpen, closeCreateMeetingModal, onMeetingCreate]);

	const onMeetingRecSeettingsSubmit = useCallback(async (editedMeetingInfo: MeetingRecordingSettings) => {
		await apiInstance.editMeetingRecordingInfo(editedMeetingInfo.id, editedMeetingInfo);
	}, []);

	const editMeetingRecSettingsModalView = useMemo((): JSX.Element => {
		return isEditMeetingRecSettingsModalOpen && (
			<EditMeetingRecSettingsModal
				meetingId={editMeetingRecSettingsId}
				closeModal={closeEditMeetingRecSettings}
				onSubmit={onMeetingRecSeettingsSubmit}
			/>
		);
	}, [isEditMeetingRecSettingsModalOpen, editMeetingRecSettingsId,
		closeEditMeetingRecSettings, onMeetingRecSeettingsSubmit]);

		
	const deleteMeetingModalView = useMemo(() => {
		const deleteMeeting = async (meetingId) => {
			await apiInstance.deleteMeeting(meetingId);
			await fetchMeetingsData();
		}

		return isDeleteMeetingModalOpen && (
			<DeleteMeetingModal
				meetingInfo={deleteMeetingInfo}
				closeModal={closeDeleteMeetingModal}
				onMeetingDeleteConfirm={deleteMeeting}
			/>
		);
	}, [isDeleteMeetingModalOpen, deleteMeetingInfo,
		closeDeleteMeetingModal, fetchMeetingsData])

	const contentTitleView = useMemo(() => {
		const trailing = (<Space>
			<IconButton
				icon={<PlusIconSlim />}
				theme="primary"
				iconSize="15px"
				disabled={!canCreateMeeting}
				onClick={openCreateMeedingModal}>Создать собрание</IconButton>
			</Space>
		);

		return (
			<Title trailing={trailing} theme="primary">
				Собрания
				{uploadFileModalView}
				{recordingInfoModalView}
				{editMeetingRecSettingsModalView}
				{deleteMeetingModalView}
			</Title>
		);
	}, [canCreateMeeting, uploadFileModalView,
		editMeetingRecSettingsModalView, deleteMeetingModalView,
		recordingInfoModalView, openCreateMeedingModal]);

	const paginationView = useMemo(() => {
		return (
			<Pagination
				className="meeting-list-page__pagination"
				currentPage={meetingsStoreState.paginationQuery.currentPage}
				totalPagesCount={totalMeetingsCount}
				onPageChange={onPageChange}
			/>
		);
	}, [meetingsStoreState.paginationQuery, totalMeetingsCount, onPageChange]);
	
	const contentView = useMemo(() => {	
		return (
			<div className="meeting-list-page">
				<div className="meeting-list-page__content">
					{contentTitleView}
					{
						meetingsData
							? <MeetingListTable
								meetings={meetingsData}
								allowClickInProcess={isDeveloperView}
								paginationView={paginationView}
								onMeetingAction={onMeetingAction}
								visitedMeetingIds={visitedMeetingIds}
								isBusy={meetingsDataBusy}
							/>
							: <div className='meeting-list-page__load'><Skeleton content="Загрузка..." /></div>
					}
				</div>
			</div>
		);
	}, [contentTitleView, meetingsData, paginationView, meetingsDataBusy,
		isDeveloperView, visitedMeetingIds, onMeetingAction]);

	return (
		<AppPageLayout
			content={contentView}
		/>
	);
}

export {MeetingListPage};